/**
 * @fileoverview Contact-type grouping helpers for the Day-One columns.
 *
 * Split out of `columns.tsx` (react-refresh/only-export-components) — pure
 * value helpers for the "Group: Contact type" column (MOR-840).
 */

import {
  ROLE_META,
  ROLE_ORDER,
  FIRM_CATEGORY_META,
  FIRM_CATEGORY_ORDER,
  type ContactRole,
  type FirmContactCategory,
} from "@/lib/contact-roles";
import { JOB_STATUS_LABELS, JOB_STATUS_ORDER } from "@/lib/job-statuses";
import type { JobStatus } from "@cvx/jobs/schema";
import type { DayOnePrecedentRow } from "./types";

/**
 * Grouping value for the Contact-type grouping (MOR-840). Prefixed (`cr:` /
 * `fc:`) so a job-contact role and a firm-contact category sharing a key
 * (e.g. "professional") stay distinct buckets.
 */
export function contactTypeGroupValue(row: DayOnePrecedentRow): string {
  if (row.contactRoleKind) return `cr:${row.contactRoleKind}`;
  if (row.firmContactCategory) return `fc:${row.firmContactCategory}`;
  return "__none__";
}

/** Human label for a `contactTypeGroupValue` — plural, e.g. "Directors". */
export function contactTypeGroupLabel(value: string): string {
  if (value.startsWith("cr:")) {
    const role = value.slice(3);
    return ROLE_META[role as ContactRole]?.pluralLabel ?? role;
  }
  if (value.startsWith("fc:")) {
    const category = value.slice(3);
    return (
      FIRM_CATEGORY_META[category as FirmContactCategory]?.label ?? category
    );
  }
  return "No contact";
}

/** Canonical display rank so contact-type groups follow `ROLE_ORDER`,
 *  then firm categories, with un-fanned-out rows last. */
export function contactTypeRank(value: string): number {
  if (value.startsWith("cr:")) {
    const idx = (ROLE_ORDER as ReadonlyArray<string>).indexOf(value.slice(3));
    return idx >= 0 ? idx : ROLE_ORDER.length;
  }
  if (value.startsWith("fc:")) {
    const idx = (FIRM_CATEGORY_ORDER as ReadonlyArray<string>).indexOf(
      value.slice(3),
    );
    return 100 + (idx >= 0 ? idx : FIRM_CATEGORY_ORDER.length);
  }
  return 999;
}

/** Convenience: stable list of selectable contact kinds (job-contact
 *  roles + firm-contact categories) for the filter dropdown, plus the
 *  `__none__` sentinel for non-fanned-out rows. */
export const CONTACT_FILTER_OPTIONS: Array<{
  value: string;
  label: string;
}> = [
  { value: "__none__", label: "No contact" },
  ...(
    Object.entries(ROLE_META) as [
      ContactRole,
      (typeof ROLE_META)[ContactRole],
    ][]
  ).map(([role, meta]) => ({
    value: role,
    label: meta.label,
  })),
  ...Object.entries(FIRM_CATEGORY_META).map(([category, meta]) => ({
    value: category,
    label: meta.label,
  })),
];

// ---------------------------------------------------------------------------
// Phase (job-status) grouping / filtering — derived from the template's
// `js:` tags via `listByJob` (see `queries.ts`).
// ---------------------------------------------------------------------------

/**
 * Grouping value for the Phase grouping — the row's first phase tag in
 * canonical status order, or the `__none__` sentinel for untagged documents.
 * A document can carry several phase tags; grouping needs a single bucket, so
 * the earliest phase (by `JOB_STATUS_ORDER`) wins.
 */
export function phaseGroupValue(row: DayOnePrecedentRow): string {
  const tags = row.phaseTags ?? [];
  if (tags.length === 0) return "__none__";
  const ordered = [...tags].sort(
    (a, b) => JOB_STATUS_ORDER.indexOf(a) - JOB_STATUS_ORDER.indexOf(b),
  );
  return ordered[0];
}

/** Canonical rank for sorting rows by phase — untagged rows sort last. */
export function phaseRank(value: string): number {
  if (value === "__none__") return JOB_STATUS_ORDER.length;
  const idx = (JOB_STATUS_ORDER as ReadonlyArray<string>).indexOf(value);
  return idx >= 0 ? idx : JOB_STATUS_ORDER.length;
}

/** Human label for a `phaseGroupValue` — e.g. "Day One", or "No phase". */
export function phaseGroupLabel(value: string): string {
  if (value === "__none__") return "No phase";
  return JOB_STATUS_LABELS[value] ?? value;
}

/** Selectable phases for the filter dropdown, plus the `__none__` sentinel. */
export const PHASE_FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "__none__", label: "No phase" },
  ...JOB_STATUS_ORDER.map((status: JobStatus) => ({
    value: status as string,
    label: JOB_STATUS_LABELS[status],
  })),
];
